// Pop, the bartender. Loads the dad likeness (assets/saloon/pop.glb, from the "Bartender model"
// lane: mixamorig skeleton, clips idle / talk / wipe_glass / slide_plate, jaw + blendshapes) and
// falls back to the Godot dad_bartender as a posed placeholder until it lands.
//
// Pop works the bar: he drifts along behind it to whoever takes a stool to talk, turns his head to
// them, moves his jaw while a line is spoken, and points out the gun rack when you ask about kit.

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { CAR } from '../../sim/saloon';

// the likeness lane's wrapper (client/render/pop, PR #58) owns its clips, props and face; it's optional
// so this branch builds before that one merges
interface LanePop {
  root: THREE.Object3D; ready: Promise<void>;
  play(name: 'idle' | 'talk' | 'wipe_glass' | 'slide_plate', fade?: number): void;
  setTalking(on: boolean): void; greet(): void; update(dt: number): void;
  /** lip-syncs a caption line, returns its length in seconds (newer builds of the lane) */
  say?(text: string, opts?: { wpm?: number }): number;
  stopSpeaking?(): void;
}
const laneMods = import.meta.glob('../render/pop/index.ts') as Record<string, () => Promise<{ Pop: new (url?: string) => LanePop }>>;

const BASE = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? '/';
const ASSET = BASE + 'assets/saloon/';

type Mode = 'idle' | 'talk' | 'wipe' | 'slide';

export class Pop {
  readonly group = new THREE.Group();
  private mixer: THREE.AnimationMixer | null = null;
  private actions = new Map<string, THREE.AnimationAction>();
  private current: THREE.AnimationAction | null = null;
  private bones = new Map<string, THREE.Bone>();
  private rest = new Map<string, THREE.Quaternion>();
  private morphs: { mesh: THREE.Mesh; open: number; blink: number }[] = [];
  private placeholder = false;
  private talkT = 0;
  private lookYaw = 0;
  private mode: Mode = 'wipe';
  private blinkT = 2;
  private q = new THREE.Quaternion();
  private e = new THREE.Euler();
  /** world position Pop looks at (the talker's head), or null to look down at his work */
  lookAt: THREE.Vector3 | null = null;
  /** car-local z Pop works the bar at (he drifts there behind the counter) */
  homeZ: number = CAR.popZ;
  /** world position Pop points his right arm at, and for how much longer */
  private pointAt = new THREE.Vector3();
  private pointT = 0;
  private pointW = 0;
  ready: Promise<void>;

  constructor() {
    this.group.name = 'pop';
    // the likeness rests its hands on the counter 0.30 m in front of its origin: put them on the top
    this.group.position.set(CAR.popX - 0.07, 0, CAR.popZ);
    this.group.rotation.y = -Math.PI / 2; // faces the aisle (-X)
    this.ready = this.load();
  }

  private lane: LanePop | null = null;
  private laneTalking = false;

  private async load() {
    const mod = Object.values(laneMods)[0];
    if (mod) {
      try {
        const { Pop: LaneCtor } = await mod();
        const p = new LaneCtor(ASSET + 'pop.glb');
        await p.ready;
        this.lane = p;
        this.group.add(p.root);
        p.root.traverse((o) => {
          const b = o as THREE.Bone;
          if (b.isBone) this.bones.set(b.name.replace(/^mixamorig:?/, ''), b);
          // shadow casting is the likeness's call: its hair cards and lenses must not cast (square shadows)
        });
        this.play('wipe');
        return;
      } catch { this.lane = null; }
    }
    const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
    let gltf;
    try { gltf = await loader.loadAsync(ASSET + 'pop.glb'); }
    catch { gltf = await loader.loadAsync(ASSET + 'pop_placeholder.glb'); this.placeholder = true; }
    const root = gltf.scene;
    // normalise: feet on the floor, ~1.78 m
    const box = new THREE.Box3().setFromObject(root);
    const h = box.max.y - box.min.y;
    if (h > 0.5) { const s = 1.78 / h; root.scale.multiplyScalar(s); root.position.y -= box.min.y * s; }
    root.traverse((o) => {
      const b = o as THREE.Bone;
      if (b.isBone) { this.bones.set(b.name.replace(/^mixamorig:?/, ''), b); this.rest.set(b.name, b.quaternion.clone()); }
      const m = o as THREE.Mesh;
      if (m.isMesh) {
        m.castShadow = true;
        m.frustumCulled = false;
        const d = m.morphTargetDictionary;
        if (d && m.morphTargetInfluences) this.morphs.push({ mesh: m, open: d.mouthOpen ?? -1, blink: d.blink ?? -1 });
      }
    });
    if (gltf.animations.length) {
      this.mixer = new THREE.AnimationMixer(root);
      for (const c of gltf.animations) this.actions.set(c.name.toLowerCase(), this.mixer.clipAction(c));
    }
    this.group.add(root);
    this.play('wipe');
  }

  play(mode: Mode) {
    this.mode = mode;
    if (this.lane) { this.lane.play(mode === 'wipe' ? 'wipe_glass' : mode === 'slide' ? 'slide_plate' : mode); return; }
    if (!this.mixer) return;
    const key = mode === 'wipe' ? 'wipe_glass' : mode === 'slide' ? 'slide_plate' : mode;
    const a = this.actions.get(key) ?? this.actions.get('idle');
    if (!a || a === this.current) return;
    a.reset().fadeIn(0.35).play();
    if (mode === 'slide') { a.setLoop(THREE.LoopOnce, 1); a.clampWhenFinished = true; }
    this.current?.fadeOut(0.35);
    this.current = a;
  }

  /** Pop speaks a caption line: the likeness lip-syncs it when it can, otherwise the jaw chatters for
   *  about as long as it takes to read. Returns the line's length in seconds (hold the caption that long). */
  say(line: string): number {
    const est = Math.min(6, line.length * 0.024 + 0.4);
    if (this.lane?.say) {
      const secs = this.lane.say(line);
      this.talkT = Math.max(this.talkT, secs); this.laneTalking = true; // say() owns the mouth: no setTalking
      if (this.mode !== 'slide') this.play('talk');
      return secs;
    }
    this.talk(est);
    return est;
  }

  /** seconds of talking left: the jaw works while > 0 */
  talk(seconds: number) { this.talkT = Math.max(this.talkT, seconds); if (this.mode !== 'slide') this.play('talk'); }

  /** point the right arm at a world position for a few seconds ("rack's over by the door") */
  point(at: THREE.Vector3, secs = 2.4) { this.pointAt.copy(at); this.pointT = secs; }

  private bone(n: string) { return this.bones.get(n); }

  /** turn `b` so its child (the next bone down the arm) heads at the world point, by weight w */
  private aimBone(b: THREE.Bone | undefined, child: THREE.Bone | undefined, at: THREE.Vector3, w: number) {
    if (!b || !child || !b.parent) return;
    b.updateWorldMatrix(true, true);
    const p0 = b.getWorldPosition(_a), p1 = child.getWorldPosition(_b);
    const from = p1.sub(p0).normalize();
    const to = _c.copy(at).sub(p0).normalize();
    const wq = b.getWorldQuaternion(_q1);
    const turn = _q2.setFromUnitVectors(from, to);
    const want = turn.multiply(wq); // world
    const pq = b.parent.getWorldQuaternion(_q3).invert();
    b.quaternion.slerp(pq.multiply(want), w);
  }

  private setRot(n: string, x: number, y: number, z: number) {
    const b = this.bone(n);
    if (!b) return;
    const r = this.rest.get(b.name)!;
    this.q.setFromEuler(this.e.set(x, y, z));
    b.quaternion.copy(r).multiply(this.q);
  }

  update(dt: number, time: number) {
    this.mixer?.update(dt);
    // drift along the bar (feet hidden behind the counter: a glide reads as walking over)
    const dz = this.homeZ - this.group.position.z;
    if (Math.abs(dz) > 0.002) this.group.position.z += Math.sign(dz) * Math.min(Math.abs(dz), dt * (0.6 + Math.min(1.2, Math.abs(dz))));
    const talking = this.talkT > 0;
    this.talkT = Math.max(0, this.talkT - dt);
    if (!talking && this.mode === 'talk') this.play('wipe');
    if (this.lane) {
      if (talking !== this.laneTalking) { this.laneTalking = talking; this.lane.setTalking(talking); if (talking) this.lane.greet(); else this.lane.stopSpeaking?.(); }
      this.lane.update(dt);
    }
    // head turns toward the talker (both rigs), clamped to what a neck does
    let want = 0;
    if (this.lookAt) {
      const p = this.group.getWorldPosition(_v);
      const dx = this.lookAt.x - p.x, dz = this.lookAt.z - p.z;
      const facing = this.group.rotation.y;
      want = Math.atan2(dx, dz) - facing;
      want = Math.atan2(Math.sin(want), Math.cos(want));
      want = Math.max(-0.9, Math.min(0.9, want));
    }
    this.lookYaw += (want - this.lookYaw) * Math.min(1, dt * 4);
    const jaw = talking ? Math.max(0, Math.sin(time * 15) * 0.6 + Math.sin(time * 23.7) * 0.4) : 0;
    this.blinkT -= dt;
    const blink = this.blinkT < 0.12 ? 1 : 0;
    if (this.blinkT < 0) this.blinkT = 2 + Math.random() * 3;
    for (const m of this.morphs) {
      if (m.open >= 0) m.mesh.morphTargetInfluences![m.open] = jaw * 0.7;
      if (m.blink >= 0) m.mesh.morphTargetInfluences![m.blink] = blink;
    }
    if (this.placeholder) {
      // the Godot model stands in its rest pose: bring the arms down to the bar, wipe with the right
      const wipe = this.mode === 'wipe' ? Math.sin(time * 2.2) : 0;
      this.setRot('upper_arm.L', 0.35, 0, -1.05);
      this.setRot('forearm.L', 0, 0, -0.9);
      this.setRot('upper_arm.R', 0.35 + wipe * 0.08, 0, 1.0 + wipe * 0.08);
      this.setRot('forearm.R', 0, wipe * 0.25, 0.95);
      this.setRot('spine.002', 0.06 + Math.sin(time * 1.3) * 0.01, 0, 0);
      this.setRot('head', -0.05, this.lookYaw * 0.7, 0);
      this.setRot('neck', 0, this.lookYaw * 0.3, 0);
      this.setRot('jaw', jaw * 0.18, 0, 0);
    } else if (!this.mixer && !this.lane) {
      this.setRot('Head', 0, this.lookYaw * 0.65, 0);
      this.setRot('Neck', 0, this.lookYaw * 0.35, 0);
      this.setRot('Jaw', jaw * 0.2, 0, 0);
    } else {
      const head = this.bone('Head'), neck = this.bone('Neck');
      if (head) { this.q.setFromEuler(this.e.set(0, this.lookYaw * 0.65, 0)); head.quaternion.multiply(this.q); }
      if (neck) { this.q.setFromEuler(this.e.set(0, this.lookYaw * 0.35, 0)); neck.quaternion.multiply(this.q); }
      const j = this.bone('Jaw');
      if (j && !this.lane && !this.morphs.some((m) => m.open >= 0)) { this.q.setFromEuler(this.e.set(jaw * 0.2, 0, 0)); j.quaternion.multiply(this.q); }
    }
    // the point: the arm comes up over ~0.3 s, holds, and goes back to the glass
    this.pointT = Math.max(0, this.pointT - dt);
    this.pointW += ((this.pointT > 0 ? 1 : 0) - this.pointW) * Math.min(1, dt * 5);
    if (this.pointW > 0.01) {
      const arm = this.bone('RightArm'), fore = this.bone('RightForeArm'), hand = this.bone('RightHand');
      this.aimBone(arm, fore, this.pointAt, this.pointW * 0.95);
      this.aimBone(fore, hand, this.pointAt, this.pointW * 0.9);
    }
  }
}

const _v = new THREE.Vector3();
const _a = new THREE.Vector3(), _b = new THREE.Vector3(), _c = new THREE.Vector3();
const _q1 = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _q3 = new THREE.Quaternion();
